import { ComponentChildren } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';

import { useUser } from '../hooks/UNSAFE_useUser';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { Flex } from '../UNSAFE_Flex';
import { WindowOverlay } from '../UNSAFE_WindowOverlay';

const positions = stringLiteralArray([
  'top',
  'top-left',
  'top-right',
  'top-start',
  'top-end',
  'bottom',
  'bottom-left',
  'bottom-right',
  'bottom-start',
  'bottom-end'
]);

type Position = typeof positions[number];

type Offset =
  | number
  | {
      horizontal?: number;
      vertical?: number;
    };

type Props = {
  children: ComponentChildren;

  /**
   * Defines the fixed position where the notifications will to be placed on the screen.
   * - top-start resolves to top-left in LTR (top-right in RTL)
   * - top-end resolves to top-right in LTR (top-left in RTL)
   * - bottom-start resolves to bottom-left in LTR (bottom-right in RTL)
   * - bottom-end resolves to bottom-right in LTR (bottom-left in RTL)
   *
   * default bottom
   */
  position?: Position;

  /**
   * Defines an offset (in pixels) in the placement. Value can be supplied as a number or
   * an object with horizontal and vertical values.
   *
   * default 0
   */
  offset?: Offset;

  /**
   * The test id which is assigned to the "data-testid" for the component's element.
   * Used for test automation, it is assigned to the wrapper element rendered by the component.
   */
  testId?: string;
};

const styles = {
  notificationPosition: cssProps`${{
    position: 'absolute',
    pointerEvents: 'initial',
    width: 'max-content'
  }}`,
  ['top-left']: cssProps`${{
    left: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset)',
    top: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)'
  }}`,
  top: cssProps`${{
    left: 'calc(50% + var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset))',
    top: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)',
    transform: 'translateX(-50%)'
  }}`,
  ['top-right']: cssProps`${{
    right: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset)',
    top: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)'
  }}`,
  ['bottom-left']: cssProps`${{
    left: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset)',
    bottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)'
  }}`,
  bottom: cssProps`${{
    left: 'calc(50% + var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset))',
    bottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)',
    transform: 'translateX(-50%)'
  }}`,
  ['bottom-right']: cssProps`${{
    right: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset)',
    bottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset)'
  }}`
} as const;

const MessageLayer = ({ children, offset, testId, position = 'bottom' }: Props) => {
  const { direction } = useUser();
  const isLtr = direction === 'ltr';

  let logicalPosition;
  switch (position) {
    case 'top-start':
      logicalPosition = isLtr ? styles['top-left'] : styles['top-right'];
      break;
    case 'top-end':
      logicalPosition = isLtr ? styles['top-right'] : styles['top-left'];
      break;
    case 'bottom-start':
      logicalPosition = isLtr ? styles['bottom-left'] : styles['bottom-right'];
      break;
    case 'bottom-end':
      logicalPosition = isLtr ? styles['bottom-right'] : styles['bottom-left'];
      break;
    default:
      logicalPosition = styles[position];
      break;
  }

  const positionClasses = classNames([styles.notificationPosition, logicalPosition]);

  const offsetStyle =
    typeof offset === 'number'
      ? {
          '--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset': `${offset}px`,
          '--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset': `${offset}px`
        }
      : {
          '--oj-c-PRIVATE-DO-NOT-USE-notification-vertical-offset': `${offset?.vertical ?? 0}px`,
          '--oj-c-PRIVATE-DO-NOT-USE-notification-horizontal-offset': `${offset?.horizontal ?? 0}px`
        };

  return (
    <WindowOverlay>
      <div
        class={positionClasses}
        data-testid={testId}
        style={offset !== undefined ? offsetStyle : undefined}>
        <Flex direction="column" gap="2x">
          {children}
        </Flex>
      </div>
    </WindowOverlay>
  );
};

export type { Position, Offset };
export { MessageLayer, positions };
